/**
 * Pre-vuelo de vídeo en el CLIENTE (F3-F): antes de subir, se comprueba que el
 * navegador puede decodificar el archivo (hidden `<video>` + `loadedmetadata`)
 * y se captura la DURACIÓN y un PÓSTER (canvas a t=1s → JPEG). Todo con object
 * URLs revocadas al terminar. Si la decodificación falla, se rechaza la subida
 * con un mensaje claro (formato no soportado).
 */

export interface VideoProbeResult {
  durationSeconds: number;
  /** Póster JPEG capturado, o null si no se pudo (p.ej. vídeo protegido). */
  poster: Blob | null;
  width: number;
  height: number;
}

/** Motivo del fallo de pre-vuelo (la UI lo traduce; sin texto en la lib). */
export type VideoDecodeReason = "decode" | "timeout";

export class VideoDecodeError extends Error {
  constructor(readonly reason: VideoDecodeReason) {
    super(reason);
    this.name = "VideoDecodeError";
  }
}

const POSTER_TIME_SECONDS = 1;
const POSTER_QUALITY = 0.82;
const PROBE_TIMEOUT_MS = 15_000;

/**
 * Comprueba decodificación + captura duración y póster. Lanza `VideoDecodeError`
 * si el navegador no puede reproducir el archivo.
 */
export function probeVideo(file: File): Promise<VideoProbeResult> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;
    video.src = url;

    let settled = false;
    const cleanup = () => {
      URL.revokeObjectURL(url);
      video.removeAttribute("src");
      video.load();
    };
    const fail = (reason: VideoDecodeReason) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer);
      cleanup();
      reject(new VideoDecodeError(reason));
    };
    const succeed = (result: VideoProbeResult) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer);
      cleanup();
      resolve(result);
    };

    const timer = setTimeout(() => fail("timeout"), PROBE_TIMEOUT_MS);

    video.addEventListener("error", () => fail("decode"));

    video.addEventListener("loadedmetadata", () => {
      const durationSeconds = Number.isFinite(video.duration) ? video.duration : 0;
      const width = video.videoWidth;
      const height = video.videoHeight;
      // Buscar el frame del póster; al completar el seek se dibuja al canvas.
      const seekTarget = Math.min(POSTER_TIME_SECONDS, Math.max(0, durationSeconds - 0.1));
      video.addEventListener(
        "seeked",
        () => {
          void capturePoster(video, width, height).then((poster) =>
            succeed({ durationSeconds, poster, width, height }),
          );
        },
        { once: true },
      );
      try {
        video.currentTime = seekTarget;
      } catch {
        succeed({ durationSeconds, poster: null, width, height });
      }
    });
  });
}

async function capturePoster(
  video: HTMLVideoElement,
  width: number,
  height: number,
): Promise<Blob | null> {
  if (width === 0 || height === 0) {
    return null;
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return null;
  }
  try {
    ctx.drawImage(video, 0, 0, width, height);
  } catch {
    // Vídeo con CORS taint u origen protegido → sin póster.
    return null;
  }
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", POSTER_QUALITY);
  });
}

/** Extensiones/MIME aceptadas por el input de archivo (mp4/webm v1). */
export const ACCEPTED_VIDEO_TYPES = "video/mp4,video/webm";

/** ¿El archivo tiene un tipo de vídeo aceptado (mp4/webm)? */
export function isAcceptedVideoType(file: File): boolean {
  return file.type === "video/mp4" || file.type === "video/webm";
}
